<template>
  <div>
    <Head title="Pick-up Queue" />
    <h1 class="mb-8 text-3xl font-bold">Pick-up Queue</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="search.search" class="mr-4 w-full max-w-md" @reset="reset" />
      <button class="btn-orange text-white-400 hover:text-white-600" @click="searchOrders">Search</button>
      <a class="btn-orange hidden" href="#" @click="openCreateOrderPrompt">
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;Order</span>
      </a>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Order Date</th>
          <th class="pb-3 pt-3 px-3">Delivery Date</th>
          <th class="pb-3 pt-3 px-3">Shipping Type</th>
          <th class="pb-3 pt-3 px-3">Order Number</th>
          <th class="pb-3 pt-3 px-3">Customer</th>
          <th class="pb-3 pt-3 px-3">Contact</th> 
          <th class="pb-3 pt-3 px-3">Alternative Contact</th> 
          <th class="pb-3 pt-3 px-3">Physical Address</th> 
          <th class="pb-3 pt-3 px-3">City</th>
          <th class="pb-3 pt-3 px-3">Region</th>
          <th class="pb-3 pt-3 px-3">Pickup Location</th>
          <th class="pb-3 pt-3 px-3 items-end justify-end">Voucher Amount</th>
          <th class="pb-3 pt-3 px-3 items-end justify-end">Total Amount</th>
          <th class="pb-3 pt-3 px-3">Agent</th>
          <th class="pb-3 pt-3 px-3">Status</th>
          <th class="pb-3 pt-3 px-3 hidden" />
        </tr>
        <tr v-for="order in orders.data" :key="order.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.date }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.delivery_date }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.shipping_type }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.order_no }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.customer }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.contact }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.alternative_contact }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.physical_address }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.city }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.region }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.pickup_location }}
            </Link>
          </td>
          <td v-if="order.voucherInfo && order.voucherInfo.code && order.has_discount_voucher == 0" class="border-t">
            <Link class="flex items-end justify-end px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.voucherInfo.voucher_amount }}
            </Link>
          </td>
          <td v-else class="border-t">
            <Link class="flex items-end justify-end px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              0.00
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-end justify-end px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.amount }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.agent }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/pick-up-queue/${order.id}/show`">
              {{ order.status }}
            </Link>
          </td>
          <td class="w-px border-t hidden">
            <a class="flex items-center px-3" :href="`/pick-up-queue/${order.id}/show`" tabindex="-1">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
            </a>
          </td>
        </tr>
        <tr v-if="orders.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="9">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="orders.links" />
  </div>
</template>
  
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import TextInput from '@/Shared/TextInput'
import Multiselect from '@vueform/multiselect'
import SelectInput from '@/Shared/SelectInput'
import axios from 'axios'
  
export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
    TextInput,
    Multiselect,
    SelectInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    orders: Object,
    customers: Object,
    sources: Object,
    cities: Object,
    errors: Object,
    skus: Object,
    payment_details: Array,
    shipping_types: Array,
    points_of_sale: Array,
  },
  data() {
    return {
      formDat: {
        order_id: null,
        order_no: null,
        phone_number: null,
        amount: null,
        customer_name: null,
      },
      search: {
        search: this.filters.search,
      },
      openCreateOrder: false,
      openDetail: false,
      openMarkInvoicedPrompt: false,
      openIssueToDispatch: false,
      openInvoice: false,
      qtyOrdered: null,
      stepOne: true,
      stepTwo: false,
      listDetails: [],
      listSkuIssuances: [],
      orderObj: [],
      orderObjTest: [],
      selectedCustomer: null,
      selectedSku: null,
      selectedSkuId: null,
      selectedOrderItemId: null,
      issueData: {
        'item_id': null,
        'quantity': null,
      },
      invoiceData: {},
      invoiceobj: {},
      askDelete: false,
      showAddPayment: false,
      isConfirmPaymentOpen: false,
      isMakePaymentOpen: false,
      mpesa_response: null,
      status: 0,
      merchantRequestId: null,
      amountError: null,
      phonenumberError: null,
      loading: false,
      complete: null,
      message: null,
      openMarkInvoiceAsDeliveredPrompt: false,
    }
  },
  computed: {
    amountToPay() {
      const totalPaid = this.orderObj.total_payments ? this.orderObj.total_payments.replace(/,/g,"") : 0
      const amountToPay = this.orderObj.amount ? this.orderObj.amount.replace(/,/g,""): 0
      const total = (amountToPay - totalPaid)
      this.formDat.amount = total
      return total
    },
    user() {
      return this.$inertia.page.props.auth.user
    },
    totalOrder() {
      let totOrder = 0
      if (this.orderObj.details) {
        this.orderObj.details.forEach((amount)=>{ 
          totOrder += parseFloat(amount.amount)
        })
      }
      return totOrder
    },
    countOrderDetails() {
      if (this.formData.details.length === 0) {
        return 0
      }
      return this.formData.details.length - 1
    },
  },
  // watch: {
  //   search: {
  //     deep: true,
  //     handler: throttle(function () {
  //       this.$inertia.get('/pick-up-queue', pickBy(this.search), { preserveState: true })
  //     }, 150),
  //   },
  // },
  methods: {
    reset() {
      this.search = mapValues(this.search, () => null)
    },
    searchOrders() {
      this.$inertia.get('/pick-up-queue', pickBy(this.search), { preserveState: true })
    },
    markInvoicedPrompt() {
      this.openMarkInvoicedPrompt = true
    },
    markInvoiceAsDeliveredPrompt() {
      this.openMarkInvoiceAsDeliveredPrompt = true
    },
    previewInvoicePrompt(item) {
      this.openInvoice = true
      this.invoiceobj = item.invoice
    },
    openCreateOrderPrompt() {
      this.openCreateOrder = true
    },
    closeModal() {
      this.openCreateOrder = false
      this.openDetail = false
      this.openMarkInvoicedPrompt = false
      this.openInvoice = false
    },
    closeDeliveredModal() {
      this.openCreateOrder = false
      this.openDetail = false
      this.openMarkInvoicedPrompt = false
      this.openInvoice = false
      this.openMarkInvoiceAsDeliveredPrompt = false
    },
    makePaymentModel(order) {
      this.formDat.order_id = order.id
      this.formDat.customer_name = order.customer
      this.formDat.order_on = order.order_no
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = false
      this.isMakePaymentOpen = true
    },
    closePaymentModal() {
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isMakePaymentOpen = false
      this.mpesa_response = null
      this.formDat.phone_number = null
      this.isConfirmPaymentOpen = false
      this.merchantRequestId = null
      this.message = null
      this.complete = null
      // window.location.reload()
    },
    closeConfirmMakePaymentModel() {
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.openEditOrder = false
      this.isMakePaymentOpen = true
      this.isConfirmPaymentOpen = false
    },
    openDetailPrompt(item) {
      this.openDetail = true
      this.listDetails = item.details
      this.orderObj = item
    },
    submitMarkInvoiced() {
      this.invoiceData.order_id = this.orderObj.id
      this.invoiceData._method = 'POST'
      this.$inertia.post('invoice-online-order', this.invoiceData, {
        onSuccess: () => {
          this.openDetail = false
          this.openMarkInvoicedPrompt = false
        },
      })
    },
    submitMarkInvoiceAsDelivered() {
      this.invoiceData.order_id = this.orderObj.id
      this.invoiceData._method = 'POST'
      this.$inertia.post('invoice-online-order-delivered', this.invoiceData, {
        onSuccess: () => {
          this.openDetail = false
          this.openMarkInvoicedPrompt = false
          this.openMarkInvoiceAsDeliveredPrompt = false
        },
      })
    },
    makePaymentOrder() {
      const data = {}
      data.order_id = this.formDat.order_id
      data.phone_number = this.formDat.phone_number
      data.amount = this.formDat.amount
      data._method = 'POST'
      const self = this
      self.loading = true;
      this.isConfirmPaymentOpen = false
      axios.post('/payments/process', data).then(response => {
        if (response) {
          self.show = true
          self.mpesa_response = response.data.message
          self.status = response.data.status
          self.stepOne = true
          self.stepTwo = false
          self.openCreateOrder = false
          self.isMakePaymentOpen = true
          self.isConfirmPaymentOpen = false
          self.merchantRequestId = response.data.merchant_request_id
        }
      }).catch(function(error) {
        if (error.response.status === 422) {
          const { amount, phone_number } = error.response.data.errors
          if (phone_number) {
            const [n] = phone_number;
            self.phonenumberError = n
          }
          if (amount) {
            const [a] = amount;
            self.amountError = a
          }
        }
        self.isConfirmPaymentOpen = false
        self.isMakePaymentOpen = true
      }).then(() => {
        self.loading = false;
      })
    },
    confirmMakePaymentModel() {
      this.openCreateOrder = false
      this.openDetail = false
      this.askDelete = false
      this.showAddPayment = false
      this.isConfirmPaymentOpen = true
      this.isMakePaymentOpen = true
      this.mpesa_response = null
      this.loading = false
      this.status = 0
    },
    checkPayment() {
      const data = {}
      data.merchant_request_id = this.merchantRequestId
      const self = this
      axios.post('/payments/check-payment', data).then(response => {
        if (response) {
          self.message = response.data.message
          self.complete = response.data.status
          self.isMakePaymentOpen = true
          self.status = null
          self.mpesa_response= null
        }
      }).catch(function(error) {

      })
    },
    showNext() {
      this.stepOne = false
      this.stepTwo = true
    },
    showBack() {
      this.stepOne = true
      this.stepTwo = false
    },
    addItem(e) {
      e.preventDefault()
      this.formData.details.push({
        part_number: null,
        quantity: null,
      })
    },
    removeItem(index, details) {
      details.splice(index, 1)
    },
    showValidationErrors(value) {
      if (this.$props.errors) {
        const error = this.$props.errors[value] || ''
        return error || ''
      }
      return ''
    },
    onSelect(id, item) {
      this.selectedCustomer = item.label
    },
    closeIssuanceModal() {
      this.openIssueToDispatch = false
      this.openInvoice = false
    },
    url() {
      let query = ''
      if (this.orderObj) {
        query += `oid=${this.orderObj.id}`
      }
      if (query) {
        query = `?${query}`
      }
      return query
    },
  },
}
</script>
  <style src="@vueform/multiselect/themes/default.css"></style>
  <style type="text/css">
    .multiselect-option.is-selected.is-pointed {
      background: var(--ms-option-bg-selected-pointed,#fb923c);
      color: var(--ms-option-color-selected-pointed,#fff);
    }
    .multiselect-option.is-selected {
      background: var(--ms-option-bg-selected-pointed,#fb923c);
      color: var(--ms-option-color-selected-pointed,#fff);
    }
    @media print {
      body * {
        /*visibility: hidden;*/
      }
      .invoice-print {
        color: #000 !important;
      }
    }
  </style>
  <style scoped>
      .invoice-print {
        color: #000 !important;
      }
  @media print {
    .invoice-print {
        color: #000 !important;
      }
  }
  </style>
  